// src/utils/visionAssistApi.js
// Vision list: 20-session cursor pages; Vision history: 40-message cursor pages; transcript exports are full-session — 2026.09.13-v2
import { getAuth } from "firebase/auth";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const baseUrl = HOST ? `${HOST}` : "";

const VISION_SESSION_LIST_PAGE_SIZE = 20;
const VISION_MESSAGE_HISTORY_PAGE_SIZE = 40;


const VISION_API_DEBUG = (() => {
  try {
    if (process.env.REACT_APP_VISION_API_DEBUG === "1") return true;
    if (typeof window !== "undefined" && window?.localStorage) {
      return window.localStorage.getItem("VISION_API_DEBUG") === "1";
    }
    return false;
  } catch {
    return process.env.REACT_APP_VISION_API_DEBUG === "1";
  }
})();

function _dbg(...args) {
  if (!VISION_API_DEBUG) return;
  try {
    console.log(...args);
  } catch {}
}

function _isPlainObject(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}

function _safeStr(x) {
  return typeof x === "string" ? x : x == null ? "" : String(x);
}

function _trimStr(v) {
  return typeof v === "string" ? v.trim() : "";
}

function _normalizeStrArray(v) {
  try {
    if (!Array.isArray(v)) return [];
    return v.map((x) => _trimStr(_safeStr(x))).filter(Boolean);
  } catch {
    return [];
  }
}

function _uniqStrArray(arr) {
  try {
    return Array.from(new Set(_normalizeStrArray(arr)));
  } catch {
    return _normalizeStrArray(arr);
  }
}

function _redactUrl(u) {
  const s = _trimStr(u);
  if (!s) return "";
  try {
    const url = new URL(s);
    const host = url.host;
    const path = url.pathname || "";
    const last = path.split("/").filter(Boolean).slice(-1)[0] || "";
    const tail = last.length > 28 ? `…${last.slice(-28)}` : last;
    return `${url.protocol}//${host}${path ? "/…/" : ""}${tail}`;
  } catch {
    if (s.startsWith("data:")) return `data:…(len=${s.length})`;
    return s.length > 60 ? `${s.slice(0, 30)}…(len=${s.length})` : s;
  }
}

function _countArray(x) {
  return Array.isArray(x) ? x.length : 0;
}

function _previewText(t, n = 140) {
  const s = _safeStr(t).replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.length <= n ? s : `${s.slice(0, n).trim()}…`;
}

function _extractAnalyzeCounts(payload = {}) {
  const p = _isPlainObject(payload) ? payload : {};

  const frames = Array.isArray(p.frames) ? p.frames : [];
  const frame_ids = Array.isArray(p.frame_ids) ? p.frame_ids : [];
  const image_urls = Array.isArray(p.image_urls) ? p.image_urls : [];
  const images = Array.isArray(p.images) ? p.images : [];

  const att = _isPlainObject(p.attachment) ? p.attachment : null;
  const attFileUrls = Array.isArray(att?.fileUrls) ? att.fileUrls : [];

  const singleImageUrl = _trimStr(p.image_url || p.imageUrl || p.imageURL);
  const singleFrameId = _trimStr(p.frame_id || p.frameId);

  const text = _safeStr(p.user_text || p.text || p.prompt || p.message || p.instruction || "").trim();

  return {
    singleImageUrlPresent: !!singleImageUrl,
    singleFrameIdPresent: !!singleFrameId,
    framesCount: frames.length,
    frameIdsCount: frame_ids.length,
    imageUrlsCount: image_urls.length,
    imagesCount: images.length,
    attachmentFileUrlsCount: attFileUrls.length,
    textLen: text.length,
    textPreview: _previewText(text, 180),

    analyze_max_frames: p.analyze_max_frames ?? p.analyzeMaxFrames ?? null,
    analyze_chunk_size: p.analyze_chunk_size ?? p.analyzeChunkSize ?? null,
  };
}

function _debugAnalyzeRequest({ route, sessionId, payload }) {
  if (!VISION_API_DEBUG) return;

  const counts = _extractAnalyzeCounts(payload);

  const p = _isPlainObject(payload) ? payload : {};
  const assistMode = _trimStr(p.assist_mode || p.assistMode) || "";
  const clientMessageId = _trimStr(p.client_message_id || p.clientMessageId) || "";

  const imageUrl =
    _trimStr(p.image_url || p.imageUrl || p.imageURL) ||
    _trimStr(p?.attachment?.image_url || p?.attachment?.imageUrl) ||
    "";

  const frameId =
    _trimStr(p.frame_id || p.frameId) ||
    _trimStr(p?.attachment?.frame_id || p?.attachment?.frameId) ||
    "";

  const frameIds = Array.isArray(p.frame_ids) ? p.frame_ids : [];
  const imageUrls = Array.isArray(p.image_urls) ? p.image_urls : [];
  const frames = Array.isArray(p.frames) ? p.frames : [];

  _dbg("[VISION_API][ANALYZE][REQUEST]", {
    route,
    sessionId: _trimStr(sessionId),
    assist_mode: assistMode,
    client_message_id: clientMessageId,
    frame_id: frameId || null,
    image_url: imageUrl ? _redactUrl(imageUrl) : null,
    frame_ids_count: frameIds.length,
    image_urls_count: imageUrls.length,
    frames_count: frames.length,
    ...counts,
    payload_keys: Object.keys(p).sort(),
  });
}


async function getIdToken() {
  try {
    const user = getAuth()?.currentUser;
    if (!user) return "";
    return await user.getIdToken();
  } catch {
    return "";
  }
}

function _absPath(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseUrl) return path;
  if (!path.startsWith("/")) return `${baseUrl}/${path}`;
  return `${baseUrl}${path}`;
}

function _frameProxyPath(sessionId, frameId) {
  const sid = encodeURIComponent(String(sessionId || "").trim());
  const fid = encodeURIComponent(String(frameId || "").trim());
  return `/vision/session/${sid}/frame/${fid}/content`;
}



export async function visionSessionLogin(extra = {}) {
  const idToken = extra?.idToken || (await getIdToken());
  if (!idToken) throw new Error("Not authenticated.");

  const url = baseUrl ? `${baseUrl}/auth/sessionLogin` : `/auth/sessionLogin`;

  const res = await fetch(url, {
    method: "POST",
    credentials: extra?.credentials || "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });

  const txt = await res.text().catch(() => "");
  let data = {};
  try {
    data = txt ? JSON.parse(txt) : {};
  } catch {
    data = {};
  }

  if (!res.ok || data?.success === false) {
    const reason = (data && (data.error || data.message || data.reason)) || res.statusText || `HTTP ${res.status}`;
    const err = new Error(typeof reason === "string" ? reason : "Session login failed");
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}

export async function visionSessionLogout(extra = {}) {
  const url = baseUrl ? `${baseUrl}/auth/sessionLogout` : `/auth/sessionLogout`;

  const res = await fetch(url, {
    method: "POST",
    credentials: extra?.credentials || "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });

  const txt = await res.text().catch(() => "");
  let data = {};
  try {
    data = txt ? JSON.parse(txt) : {};
  } catch {
    data = {};
  }

  if (!res.ok || data?.success === false) {
    const reason = (data && (data.error || data.message || data.reason)) || res.statusText || `HTTP ${res.status}`;
    const err = new Error(typeof reason === "string" ? reason : "Session logout failed");
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}



async function _maybeEnsureSessionCookie(extra = {}) {
  try {
    if (!extra?.ensureSessionCookie) return;
    // Only in browser context; SSR doesn't have cookies anyway.
    if (typeof window === "undefined") return;
    // Create/refresh session cookie using current ID token
    await visionSessionLogin(extra);
  } catch {
    // swallow: print/download should still work if backend embeds images
  }
}


function _hasHeader(headersObj, name) {
  try {
    const n = String(name || "").toLowerCase();
    for (const k of Object.keys(headersObj || {})) {
      if (String(k).toLowerCase() === n) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Helper: authorized fetch to our backend (JSON)
 * - credentials:"include"
 * - Authorization Bearer token
 */
async function authedFetch(path, opts = {}, extra = {}) {
  const overrideToken = extra?.idToken;
  const token = overrideToken || (await getIdToken());
  if (!token) throw new Error("Not authenticated.");

  const headers = { ...(opts.headers || {}) };
  headers["Authorization"] = `Bearer ${token}`;

  const method = (opts.method || "GET").toUpperCase();
  const hasBody = typeof opts.body !== "undefined" && opts.body !== null;

  let isFormData = false;
  try {
    isFormData = hasBody && typeof FormData !== "undefined" && opts.body instanceof FormData;
  } catch {
    isFormData = false;
  }

  if (hasBody && !isFormData && !_hasHeader(headers, "Content-Type") && method !== "GET") {
    headers["Content-Type"] = "application/json";
  }

  const url = baseUrl ? `${baseUrl}${path}` : path;

  let t0 = 0;
  if (VISION_API_DEBUG) {
    try {
      t0 = performance?.now ? performance.now() : Date.now();
    } catch {
      t0 = Date.now();
    }
  }

  const res = await fetch(url, {
    ...opts,
    headers,
    credentials: extra?.credentials || "include",
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (VISION_API_DEBUG) {
    try {
      const t1 = performance?.now ? performance.now() : Date.now();
      const ms = Math.max(0, (t1 || 0) - (t0 || 0));
      const isAnalyze = typeof path === "string" && path.includes("/vision/session/") && path.endsWith("/analyze");

      if (isAnalyze) {
        _dbg("[VISION_API][ANALYZE][RESPONSE]", {
          route: path,
          ok: !!res.ok,
          status: res.status,
          ms: Math.round(ms),
          resp_keys: _isPlainObject(data) ? Object.keys(data).sort() : [],
        });
      }
    } catch {}
  }

  const notOk = !res.ok || data?.ok === false || data?.success === false;
  if (notOk) {
    let reason = res.statusText || `HTTP ${res.status}`;
    const errPayload = data?.error;

    if (typeof errPayload === "string") {
      reason = errPayload;
    } else if (errPayload && typeof errPayload === "object") {
      reason = errPayload.reason || errPayload.message || errPayload.code || data?.reason || data?.message || reason;
    } else if (typeof data?.reason === "string") {
      reason = data.reason;
    } else if (typeof data?.message === "string") {
      reason = data.message;
    } else if (text && typeof text === "string" && text.trim()) {
      reason = text.trim().slice(0, 500);
    }

    const err = new Error(typeof reason === "string" ? reason : "Request failed");
    err.response = { status: res.status, data };

    if (errPayload && typeof errPayload === "object") {
      if (errPayload.code) err.code = errPayload.code;
      if (errPayload.billingSnapshot) err.billingSnapshot = errPayload.billingSnapshot;
    } else if (data?.billingSnapshot) {
      err.billingSnapshot = data.billingSnapshot;
    }

    throw err;
  }

  return data;
}

/**
 * Helper: authorized fetch returning raw Response (HTML/PDF)
 * - credentials:"include"
 */
async function authedFetchRaw(path, opts = {}, extra = {}) {
  const overrideToken = extra?.idToken;
  const token = overrideToken || (await getIdToken());
  if (!token) throw new Error("Not authenticated.");

  const headers = { ...(opts.headers || {}) };
  headers["Authorization"] = `Bearer ${token}`;

  const method = (opts.method || "GET").toUpperCase();
  const hasBody = typeof opts.body !== "undefined" && opts.body !== null;

  let isFormData = false;
  try {
    isFormData = hasBody && typeof FormData !== "undefined" && opts.body instanceof FormData;
  } catch {
    isFormData = false;
  }

  if (hasBody && !isFormData && !_hasHeader(headers, "Content-Type") && method !== "GET") {
    headers["Content-Type"] = "application/json";
  }

  const url = baseUrl ? `${baseUrl}${path}` : path;

  const res = await fetch(url, {
    ...opts,
    headers,
    credentials: extra?.credentials || "include",
  });

  if (!res.ok) {
    let reason = res.statusText || `HTTP ${res.status}`;
    try {
      const txt = await res.text();
      if (txt && txt.trim()) reason = txt.trim().slice(0, 500);
    } catch {}
    const err = new Error(reason || "Request failed");
    err.response = { status: res.status };
    throw err;
  }

  return res;
}



function _coalesceStr(...vals) {
  for (const v of vals) {
    const s = _trimStr(v);
    if (s) return s;
  }
  return "";
}

function _coalesceAny(...vals) {
  for (const v of vals) {
    if (v === null || typeof v === "undefined") continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return null;
}

function _uuidLike() {
  try {
    if (typeof crypto !== "undefined" && crypto?.randomUUID) return crypto.randomUUID();
  } catch {}
  return `cm_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function _pickClientCreatedAt(meta) {
  try {
    if (!_isPlainObject(meta)) return 0;
    const v =
      meta.client_created_at ??
      meta.clientCreatedAt ??
      meta.client_created_at_ms ??
      meta.clientCreatedAtMs ??
      0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function metaObjOrEmpty(x) {
  return _isPlainObject(x) ? x : {};
}


function _normalizeAnalyzePayload(input = {}) {
  const p = _isPlainObject(input) ? input : {};

  const assist_mode = _coalesceStr(p.assist_mode, p.assistMode) || "B";
  const client_message_id = _coalesceStr(p.client_message_id, p.clientMessageId) || _uuidLike();

  const meta = _isPlainObject(p.meta) ? p.meta : {};
  const clientCreatedAt = _pickClientCreatedAt(meta);

  const image_url = _coalesceStr(p.image_url, p.imageUrl, p.imageURL);
  const frame_id = _coalesceStr(p.frame_id, p.frameId);

  const session_doc_path = _coalesceStr(p.session_doc_path, p.sessionDocPath, p.session_doc, p.sessionDoc);

  const frame_ids = _uniqStrArray(p.frame_ids || p.frameIds || p.frames_ids || p.framesIds || []);
  const image_urls = _uniqStrArray(p.image_urls || p.imageUrls || p.images_urls || p.imagesUrls || []);

  const attIn = _isPlainObject(p.attachment) ? p.attachment : null;
  const attFileUrls = _uniqStrArray(attIn?.fileUrls || attIn?.file_urls || []);
  const attFileUrlSingle = _trimStr(attIn?.fileUrl || attIn?.file_url || "");

  const allAttachmentUrls = _uniqStrArray([...attFileUrls, ...(attFileUrlSingle ? [attFileUrlSingle] : [])]);

  const frames = Array.isArray(p.frames) ? p.frames : [];
  const text = _safeStr(p.text || p.user_text || p.prompt || p.message || p.instruction || "").trim();

  const bucket = _coalesceStr(p.bucket, p.frame_bucket);
  const object_name = _coalesceStr(p.object_name, p.objectName, p.frame_object_name);

  const passthrough = {};
  if (_isPlainObject(p)) {
    for (const [k, v] of Object.entries(p)) {
      if (
        k === "image_url" ||
        k === "imageUrl" ||
        k === "imageURL" ||
        k === "frame_id" ||
        k === "frameId" ||
        k === "frame_ids" ||
        k === "frameIds" ||
        k === "image_urls" ||
        k === "imageUrls" ||
        k === "frames" ||
        k === "session_doc_path" ||
        k === "sessionDocPath" ||
        k === "session_doc" ||
        k === "sessionDoc" ||
        k === "assist_mode" ||
        k === "assistMode" ||
        k === "client_message_id" ||
        k === "clientMessageId" ||
        k === "meta" ||
        k === "bucket" ||
        k === "frame_bucket" ||
        k === "object_name" ||
        k === "objectName" ||
        k === "frame_object_name" ||
        k === "attachment" ||
        k === "text" ||
        k === "user_text" ||
        k === "prompt" ||
        k === "message" ||
        k === "instruction"
      ) {
        continue;
      }
      passthrough[k] = v;
    }
  }

  // Keep singular and plural analyze targets distinct.
  // A multi-frame/image request must never manufacture a singular target from
  // the final array element, because that gives downstream code two competing
  // authorities for the same analyze request.
  return {
    image_url,
    frame_id,
    frame_ids,
    image_urls,
    frames,
    attachment: attIn || null,

    session_doc_path,
    assist_mode,
    client_message_id,
    meta: {
      ...(metaObjOrEmpty(meta) || {}),
      ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
    },
    bucket,
    object_name,

    text,

    attachment_file_urls: allAttachmentUrls,

    passthrough,
  };
}

function _normalizeUploadResponse(resp = {}) {
  const r = _isPlainObject(resp) ? resp : {};
  const image_url = _coalesceStr(r.image_url, r.imageUrl, r.signed_url, r.fallback_token_url);
  const frame_id = _coalesceStr(r.frame_id, r.frameId);
  const session_doc_path = _coalesceStr(r.session_doc_path, r.sessionDocPath);
  return { ...r, image_url, frame_id, session_doc_path };
}

function _normalizeFrameUrlResponse(resp = {}) {
  const r = _isPlainObject(resp) ? resp : {};
  const image_url = _coalesceStr(r.image_url, r.imageUrl);
  const image_proxy_url = _coalesceStr(r.image_proxy_url, r.imageProxyUrl, r.proxy_url, r.proxyUrl);

  const frame_id = _coalesceStr(r.frame_id, r.frameId);
  const bucket = _coalesceStr(r.bucket);
  const object_name = _coalesceStr(r.object_name, r.objectName);
  const content_type = _coalesceStr(r.content_type, r.contentType);
  const access_kind = _coalesceStr(r.access_kind, r.accessKind);

  return {
    ...r,
    image_url: image_url || "",
    image_proxy_url: image_proxy_url ? _absPath(image_proxy_url) : "",
    frame_id,
    bucket,
    object_name,
    content_type,
    access_kind,
  };
}

function _normalizeFramesListResponse(resp = {}) {
  const r = _isPlainObject(resp) ? resp : {};
  const items = Array.isArray(r.items) ? r.items : [];
  const normItems = items.map((it) => {
    const d = _isPlainObject(it) ? it : {};
    const frame_id = _coalesceStr(d.frame_id, d.frameId);
    const image_url = _coalesceStr(d.image_url, d.imageUrl, d.thumbnail_url, d.thumbnailUrl);
    const image_proxy_url = _coalesceStr(d.image_proxy_url, d.imageProxyUrl, d.proxy_url, d.proxyUrl);

    const thumbnail_url = _coalesceStr(d.thumbnail_url, d.thumbnailUrl, d.image_url, d.imageUrl);
    const bucket = _coalesceStr(d.bucket);
    const object_name = _coalesceStr(d.object_name, d.objectName);
    const content_type = _coalesceStr(d.content_type, d.contentType);
    const created_at = _coalesceStr(d.created_at);
    const created_at_ms = _coalesceAny(d.created_at_ms);
    const access_kind = _coalesceStr(d.access_kind, d.accessKind);

    return {
      ...d,
      frame_id,
      image_url: image_url || "",
      image_proxy_url: image_proxy_url ? _absPath(image_proxy_url) : "",
      thumbnail_url,
      bucket,
      object_name,
      content_type,
      created_at,
      created_at_ms,
      access_kind,
    };
  });

  return { ...r, items: normItems };
}


export function resolveVisionFirestorePaths(sessionId, sessionData = null) {
  const sid = (sessionId || "").trim();
  void sid;

  const d = sessionData && typeof sessionData === "object" ? sessionData : {};

  const sessionDoc = String(d.session_doc_path || "").trim() || "";

  const messagesCol =
    String(d.messages_col_path || "").trim() ||
    String(d.messagesColPath || d.messages_collection_path || d.messages_path || "").trim() ||
    "";

  const framesCol =
    String(d.frames_col_path || "").trim() ||
    String(d.framesColPath || d.frames_collection_path || d.frames_path || "").trim() ||
    "";

  return {
    sessionDoc: sessionDoc || "",
    messagesCol: messagesCol || "",
    framesCol: framesCol || "",
  };
}



export async function startVisionSession({ formType, siteHost, goal, viewMode }, extra = {}) {
  const payload = {
    form_type: formType || "",
    site_host: siteHost || "",
    goal: goal || "",
  };
  if (viewMode) payload.view_mode = viewMode;

  return authedFetch(`/vision/session/start`, { method: "POST", body: JSON.stringify(payload) }, extra);
}

export async function getVisionSession(sessionId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return authedFetch(`/vision/session/${encodeURIComponent(sessionId)}`, { method: "GET" }, extra);
}

export async function getVisionSessionWithPaths(sessionId, extra = {}) {
  const data = await getVisionSession(sessionId, extra);
  return { ...data, firestorePaths: resolveVisionFirestorePaths(sessionId, data) };
}

export async function pingVisionSession(sessionId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/ping`,
    { method: "POST", body: JSON.stringify({}) },
    extra
  );
}

export async function pauseVisionSession(sessionId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/pause`,
    { method: "POST", body: JSON.stringify({}) },
    extra
  );
}

export async function resumeVisionSession(sessionId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/resume`,
    { method: "POST", body: JSON.stringify({}) },
    extra
  );
}

export async function stopVisionSession(sessionId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/stop`,
    { method: "POST", body: JSON.stringify({}) },
    extra
  );
}

export async function getVisionUsage(extra = {}) {
  return authedFetch(`/vision/usage`, { method: "GET" }, extra);
}



export async function listVisionSessions(
  {
    searchTerm = "",
    includeArchived = true,
    includeDeleted = false,
    pageSize = VISION_SESSION_LIST_PAGE_SIZE,
    cursor = "",
    order = "desc",
    includePreview = true,
    includeLatestThumb = false,
  } = {},
  extra = {}
) {
  const requestedPageSize = Number(pageSize);
  const finalPageSize = Number.isFinite(requestedPageSize)
    ? Math.max(1, Math.min(VISION_SESSION_LIST_PAGE_SIZE, requestedPageSize))
    : VISION_SESSION_LIST_PAGE_SIZE;

  const payload = {
    search_term: String(searchTerm || "").trim(),
    include_archived: !!includeArchived,
    include_deleted: !!includeDeleted,
    include_preview: !!includePreview,
    include_latest_thumb: !!includeLatestThumb,
    page_size: finalPageSize,
    cursor: _trimStr(cursor),
    order: String(order || "desc").toLowerCase() === "asc" ? "asc" : "desc",
  };

  return authedFetch(
    `/vision/sessions/list`,
    { method: "POST", body: JSON.stringify(payload) },
    extra
  );
}

async function _getVisionMessagesImpl(sessionId, extra = {}, opts = {}) {
  const requestedPageSize = Number(opts?.pageSize ?? opts?.limit);
  const pageSize = Number.isFinite(requestedPageSize) && requestedPageSize > 0
    ? Math.max(1, Math.min(VISION_MESSAGE_HISTORY_PAGE_SIZE, requestedPageSize))
    : VISION_MESSAGE_HISTORY_PAGE_SIZE;

  const cursor = _trimStr(
    opts?.cursor ||
    opts?.beforeMessageId ||
    opts?.before_message_id ||
    opts?.nextCursor ||
    opts?.next_cursor ||
    ""
  );

  const includeFrameUrls = opts?.includeFrameUrls === true;
  const includeFrameProxy = opts?.includeFrameProxy === true;

  const params = new URLSearchParams();
  params.set("page_size", String(pageSize));
  if (cursor) params.set("cursor", cursor);

  const resp = await authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/messages?${params.toString()}`,
    { method: "GET" },
    extra
  );

  const msgs = Array.isArray(resp?.messages) ? resp.messages : [];
  const normalized = [];

  for (const m of msgs) {
    const d = _isPlainObject(m) ? { ...m } : {};
    const fid = _trimStr(d.frame_id || d.frameId);

    if (_trimStr(d.image_proxy_url || d.imageProxyUrl)) {
      d.image_proxy_url = _absPath(d.image_proxy_url || d.imageProxyUrl);
    }

    if (_trimStr(d.image_url || d.imageUrl)) {
      d.image_url = _trimStr(d.image_url || d.imageUrl);
    }

    if (includeFrameProxy && fid && !_trimStr(d.image_proxy_url || d.imageProxyUrl)) {
      d.image_proxy_url = _absPath(_frameProxyPath(sessionId, fid));
    }

    // The canonical history endpoint intentionally returns frame pointers, not
    // a bulk of fresh signed URLs. Preserve the prior optional behavior only
    // when a caller explicitly requests frame URLs.
    if (includeFrameUrls && fid && !_trimStr(d.image_url || d.imageUrl)) {
      try {
        const resolved = await _getVisionFrameUrlImpl(sessionId, fid, extra);
        const fresh = _trimStr(resolved?.image_url || resolved?.imageUrl);
        const proxy = _trimStr(
          resolved?.image_proxy_url ||
          resolved?.imageProxyUrl
        );
        if (fresh) d.image_url = fresh;
        if (proxy && !_trimStr(d.image_proxy_url)) {
          d.image_proxy_url = _absPath(proxy);
        }
      } catch {
        // Keep the canonical frame pointer. The interface can re-resolve later.
      }
    }

    d.message_id = d.message_id || d.messageId || d.id || null;
    normalized.push(d);
  }

  return {
    ...resp,
    messages: normalized,
    page_size: Number(resp?.page_size || pageSize) || pageSize,
    has_more: resp?.has_more === true,
    next_cursor: _trimStr(resp?.next_cursor) || null,
  };
}

export async function getVisionMessages(sessionId, extra = {}, opts = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  return _getVisionMessagesImpl(sessionId, extra, opts);
}


async function _getVisionFrameUrlImpl(sessionId, fid, extra = {}) {
  try {
    const resp = await authedFetch(
      `/vision/session/${encodeURIComponent(sessionId)}/frame/${encodeURIComponent(fid)}`,
      { method: "GET" },
      extra
    );

    const norm = _normalizeFrameUrlResponse({ ...resp });
    // If backend didn't include proxy, we can still provide the content route as proxy fallback
    if (!_trimStr(norm.image_proxy_url)) {
      norm.image_proxy_url = _absPath(_frameProxyPath(sessionId, fid));
    }
    // Keep image_url as-is (signed/direct) if present
    return norm;
  } catch (e) {
    // Legacy/alt route
    const resp = await authedFetch(
      `/vision/session/${encodeURIComponent(sessionId)}/frame/url`,
      { method: "POST", body: JSON.stringify({ frame_id: fid }) },
      extra
    );
    const norm = _normalizeFrameUrlResponse({ ...resp });
    if (!_trimStr(norm.image_proxy_url)) {
      norm.image_proxy_url = _absPath(_frameProxyPath(sessionId, fid));
    }
    return norm;
  }
}

export async function getVisionFrameUrl(sessionId, frameId, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  const fid = _trimStr(frameId);
  if (!fid) throw new Error("Missing frameId.");
  return _getVisionFrameUrlImpl(sessionId, fid, extra);
}

export async function listVisionFrames(
  sessionId,
  { limit = 50, order = "desc", includeUrls = false, messagesLimit = 500 } = {},
  extra = {}
) {
  if (!sessionId) throw new Error("Missing sessionId.");

  const lim = Number.isFinite(Number(limit))
    ? Math.max(1, Math.min(200, Number(limit)))
    : 50;
  const ord =
    String(order || "desc").toLowerCase().trim() === "asc" ? "asc" : "desc";
  const inc = !!includeUrls;

  // This helper derives frame IDs from message history. Since history is now
  // cursor-paginated at 40 records/page, walk pages until we have scanned the
  // requested message budget or reached the beginning of the conversation.
  const maxMessagesToScan = Number.isFinite(Number(messagesLimit))
    ? Math.max(1, Number(messagesLimit))
    : 500;

  const msgs = [];
  let cursor = "";
  let scanned = 0;

  while (scanned < maxMessagesToScan) {
    const pageSize = Math.min(
      VISION_MESSAGE_HISTORY_PAGE_SIZE,
      maxMessagesToScan - scanned
    );

    const msgResp = await getVisionMessages(sessionId, extra, {
      pageSize,
      cursor,
      includeFrameUrls: false,
      includeFrameProxy: false,
    });

    const page = Array.isArray(msgResp?.messages) ? msgResp.messages : [];
    if (!page.length) break;

    // Each page is chronological. Older cursor pages belong before the already
    // collected newer page so the combined list remains chronological.
    msgs.unshift(...page);
    scanned += page.length;

    if (msgResp?.has_more !== true) break;

    const nextCursor = _trimStr(msgResp?.next_cursor);
    if (!nextCursor || nextCursor === cursor) break;
    cursor = nextCursor;
  }

  const seen = new Set();
  const items = [];
  for (const m of msgs) {
    const d = _isPlainObject(m) ? m : {};
    const fid = _trimStr(d.frame_id || d.frameId);
    if (!fid || seen.has(fid)) continue;
    seen.add(fid);

    const created_at_ms =
      typeof d.created_at_ms === "number"
        ? d.created_at_ms
        : typeof d.created_at_ms === "string"
        ? Number(d.created_at_ms)
        : typeof d.createdAtMs === "number"
        ? d.createdAtMs
        : null;

    items.push({
      frame_id: fid,
      created_at_ms: Number.isFinite(created_at_ms) ? created_at_ms : null,
      image_proxy_url: _absPath(_frameProxyPath(sessionId, fid)),
    });
  }

  if (items.some((x) => Number.isFinite(x.created_at_ms))) {
    items.sort((a, b) => {
      const aa = Number.isFinite(a.created_at_ms) ? a.created_at_ms : 0;
      const bb = Number.isFinite(b.created_at_ms) ? b.created_at_ms : 0;
      return ord === "asc" ? aa - bb : bb - aa;
    });
  } else if (ord === "desc") {
    items.reverse();
  }

  const sliced = items.slice(0, lim);

  if (inc && sliced.length) {
    try {
      const payload = {
        frames: sliced.map((x) => ({ frame_id: x.frame_id })),
      };
      const resolvedResp = await authedFetch(
        `/vision/session/${encodeURIComponent(sessionId)}/frames/resolve`,
        { method: "POST", body: JSON.stringify(payload) },
        extra
      );
      const resolved = _isPlainObject(resolvedResp?.resolved)
        ? resolvedResp.resolved
        : {};

      const merged = sliced.map((x) => {
        const r = resolved?.[x.frame_id];
        const image_url = _isPlainObject(r)
          ? _trimStr(r.image_url || r.imageUrl)
          : "";
        const image_proxy_url = _isPlainObject(r)
          ? _trimStr(r.image_proxy_url || r.imageProxyUrl)
          : "";
        return {
          ...x,
          image_url: image_url || "",
          image_proxy_url: _absPath(image_proxy_url) || x.image_proxy_url,
        };
      });

      return _normalizeFramesListResponse({ success: true, items: merged });
    } catch {
      return _normalizeFramesListResponse({ success: true, items: sliced });
    }
  }

  return _normalizeFramesListResponse({ success: true, items: sliced });
}


export async function exportVisionTranscriptHtml(
  sessionId,
  { download = true, mode = "print" } = {},
  extra = {}
) {
  if (!sessionId) throw new Error("Missing sessionId.");

  const m = String(mode || "print").toLowerCase().trim();
  const finalMode =
    m === "download" || m === "embed" || m === "offline"
      ? "download"
      : "print";

  const payload = {
    download: !!download,
    mode: finalMode,
  };

  const res = await authedFetchRaw(
    `/vision/session/${encodeURIComponent(sessionId)}/export/html`,
    { method: "POST", body: JSON.stringify(payload) },
    extra
  );

  return await res.text();
}

export async function exportVisionTranscriptPdf(
  sessionId,
  { download = true } = {},
  extra = {}
) {
  if (!sessionId) throw new Error("Missing sessionId.");

  const payload = {
    download: !!download,
  };

  const res = await authedFetchRaw(
    `/vision/session/${encodeURIComponent(sessionId)}/export/pdf`,
    { method: "POST", body: JSON.stringify(payload) },
    extra
  );

  return await res.blob();
}

// inject <base href="..."> into export HTML so assets resolve in about:blank
function _injectBaseHref(html) {
  try {
    const s = _safeStr(html);
    if (!s.trim()) return s;

    if (/<base\s/i.test(s)) return s;

    const baseHref = _absPath("/");
    if (!baseHref) return s;

    const baseTag = `<base href="${baseHref}">`;

    if (/<head[^>]*>/i.test(s)) {
      return s.replace(/<head[^>]*>/i, (m) => `${m}\n${baseTag}`);
    }

    if (/<html[^>]*>/i.test(s)) {
      return s.replace(/<html[^>]*>/i, (m) => `${m}\n<head>${baseTag}</head>`);
    }

    return `${baseTag}\n${s}`;
  } catch {
    return _safeStr(html);
  }
}

function _injectAutoPrint(html) {
  try {
    const s = _safeStr(html);
    if (!s.trim()) return s;

    if (/window\.print\s*\(/i.test(s) && /afterprint|onafterprint|beforeprint|onbeforeprint/i.test(s)) return s;

    const js = `
<style>
@media print {
  #__vision_print_btn { display: none !important; }
}
</style>
<script>
(function(){
  function safePrint(){
    try { window.focus(); } catch(e){}
    try { window.print(); } catch(e){}
  }
  function addButton(){
    try{
      if (document.getElementById("__vision_print_btn")) return;
      var btn = document.createElement("button");
      btn.id = "__vision_print_btn";
      btn.type = "button";
      btn.textContent = "Click to Print";
      btn.style.position = "fixed";
      btn.style.top = "12px";
      btn.style.right = "12px";
      btn.style.zIndex = "999999";
      btn.style.padding = "8px 12px";
      btn.style.borderRadius = "10px";
      btn.style.border = "1px solid rgba(148,163,184,0.6)";
      btn.style.background = "white";
      btn.style.cursor = "pointer";
      btn.onclick = function(){ safePrint(); };
      document.body.appendChild(btn);
    }catch(e){}
  }
  window.addEventListener("load", function(){
    addButton();
    setTimeout(safePrint, 350);
  }, { once: true });
})();
</script>`;

    if (/<\/body>/i.test(s)) return s.replace(/<\/body>/i, `${js}\n</body>`);
    return `${s}\n${js}`;
  } catch {
    return _safeStr(html);
  }
}

async function _waitForPopupReady(win, { timeoutMs = 6000 } = {}) {
  const start = Date.now();
  const sleep = (ms) =>
    new Promise((r) => {
      try {
        setTimeout(r, ms);
      } catch {
        r();
      }
    });

  while (Date.now() - start < timeoutMs) {
    try {
      const rs = win?.document?.readyState;
      if (rs === "complete" || rs === "interactive") break;
    } catch {}
    await sleep(50);
  }

  let imgs = [];
  try {
    imgs = Array.from(win?.document?.images || []);
  } catch {
    imgs = [];
  }

  if (!imgs.length) return true;

  const done = new Set();
  const mark = (i) => {
    try {
      done.add(i);
    } catch {}
  };

  imgs.forEach((img, idx) => {
    try {
      if (img.complete) mark(idx);
      else {
        img.addEventListener("load", () => mark(idx), { once: true });
        img.addEventListener("error", () => mark(idx), { once: true });
      }
    } catch {
      mark(idx);
    }
  });

  while (Date.now() - start < timeoutMs) {
    if (done.size >= imgs.length) return true;
    await sleep(60);
  }

  return false;
}

export async function openVisionTranscriptPrintWindow(sessionId, _options = {}, extra = {}) {
  
  await _maybeEnsureSessionCookie({ ...extra, ensureSessionCookie: true });

  if (typeof window === "undefined") {
    let html0 = await exportVisionTranscriptHtml(sessionId, { download: false, mode: "print" }, extra);
    html0 = _injectBaseHref(html0);
    html0 = _injectAutoPrint(html0);
    return { html: html0 };
  }

  const w = window.open("", "_blank");
  if (!w) throw new Error("Popup blocked.");

  try {
    w.document.open();
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"/><title>Loading…</title></head><body style="font-family:system-ui;padding:16px;"><p>Loading transcript…</p></body></html>`
    );
    w.document.close();
  } catch {}

  let html = "";
  try {
    html = await exportVisionTranscriptHtml(sessionId, { download: false, mode: "print" }, extra);
  } catch (e) {
    try {
      const msg = _safeStr(e?.message || e || "Print export failed");
      w.document.open();
      w.document.write(
        `<!doctype html><html><head><meta charset="utf-8"/><title>Print Error</title></head><body style="font-family:system-ui;padding:16px;"><h3>Print Error</h3><pre style="white-space:pre-wrap">${msg}</pre></body></html>`
      );
      w.document.close();
    } catch {}
    throw e;
  }

  html = _injectBaseHref(html);
  html = _injectAutoPrint(html);

  if (!_safeStr(html).trim() || _safeStr(html).trim().length < 32) {
    try {
      w.document.open();
      w.document.write(
        `<!doctype html><html><head><meta charset="utf-8"/><title>Print Error</title></head><body style="font-family:system-ui;padding:16px;"><h3>Print Error</h3><p>Export returned empty HTML.</p></body></html>`
      );
      w.document.close();
    } catch {}
    return { ok: false, error: "empty_html" };
  }

  try {
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch {}

  try {
    await _waitForPopupReady(w, { timeoutMs: 6500 });
  } catch {}

  try {
    w.focus();
  } catch {}

  return { ok: true };
}

export async function downloadVisionTranscriptHtml(sessionId, { filename = "" } = {}, extra = {}) {
  
  await _maybeEnsureSessionCookie({ ...extra, ensureSessionCookie: true });

  let html = await exportVisionTranscriptHtml(sessionId, { download: true, mode: "download" }, extra);
  html = _injectBaseHref(html);

  if (typeof window === "undefined") return { html };

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `vision_transcript_${String(sessionId)}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(url), 2500);
  return { ok: true };
}

export async function downloadVisionTranscriptPdf(sessionId, { filename = "" } = {}, extra = {}) {
 
  await _maybeEnsureSessionCookie({ ...extra, ensureSessionCookie: true });

  const blob = await exportVisionTranscriptPdf(sessionId, { download: true }, extra);

  if (typeof window === "undefined") return { blob };

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `vision_transcript_${String(sessionId)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(url), 2500);
  return { ok: true };
}


export async function uploadVisionFrame(sessionId, fileOrWrapper, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");
  if (!fileOrWrapper) throw new Error("Missing file.");

  const file = fileOrWrapper?.file || fileOrWrapper;
  const form = new FormData();

  const isFile = typeof File !== "undefined" && file instanceof File;
  const isBlob = typeof Blob !== "undefined" && file instanceof Blob;

  if (isFile) {
    form.append("file", file, file.name);
  } else if (isBlob) {
    form.append("file", file, fileOrWrapper?.name || "frame.png");
  } else if (fileOrWrapper?.blob && typeof Blob !== "undefined" && fileOrWrapper.blob instanceof Blob) {
    form.append("file", fileOrWrapper.blob, fileOrWrapper.name || "frame.png");
  } else {
    throw new Error("Invalid file. Provide a File, Blob, { blob, name }, or { file }.");
  }

  const resp = await authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/frame/upload`,
    { method: "POST", body: form },
    extra
  );

  const norm = _normalizeUploadResponse(resp);

  if (!_trimStr(norm.image_url)) throw new Error("Frame upload missing image_url from backend.");
  if (!_trimStr(norm.frame_id)) throw new Error("Frame upload missing frame_id from backend.");
  if (!_trimStr(norm.session_doc_path)) {
    throw new Error("Frame upload missing session_doc_path from backend.");
  }

  return norm;
}



export async function analyzeVisionFrame(sessionId, payload = {}, extra = {}) {
  if (!sessionId) throw new Error("Missing sessionId.");

  const n = _normalizeAnalyzePayload(payload);

  const frame_ids = _uniqStrArray(n.frame_ids || []);
  const image_urls = _uniqStrArray(n.image_urls || []);
  const singleFrameId = _trimStr(n.frame_id);
  const singleImageUrl = _trimStr(n.image_url);

  const att = _isPlainObject(n.attachment) ? n.attachment : null;
  const attFileUrls = _uniqStrArray(att?.fileUrls || att?.file_urls || []);
  const attFileUrlSingle = _trimStr(att?.fileUrl || att?.file_url || "");
  const allAttUrls = _uniqStrArray([...attFileUrls, ...(attFileUrlSingle ? [attFileUrlSingle] : [])]);

  const hasAnything =
    !!singleFrameId ||
    !!singleImageUrl ||
    frame_ids.length > 0 ||
    image_urls.length > 0 ||
    allAttUrls.length > 0 ||
    (Array.isArray(n.frames) && n.frames.length > 0);

  if (!hasAnything) {
    throw new Error(
      "Analyze requires at least one of: frame_id, image_url, frame_ids[], image_urls[], or attachment.fileUrls."
    );
  }

  // Plural targets are authoritative for multi-screen / multi-image analysis.
  // If frame_ids/image_urls are present, do not also send a competing singular
  // frame_id/image_url. Single-screen analysis still uses the singular fields.
  const outboundFrameId = frame_ids.length ? "" : singleFrameId;
  const outboundImageUrl = image_urls.length ? "" : singleImageUrl;

  const out = {
    ...(outboundImageUrl ? { image_url: outboundImageUrl } : {}),
    ...(outboundFrameId ? { frame_id: outboundFrameId } : {}),

    ...(frame_ids.length ? { frame_ids } : {}),
    ...(image_urls.length ? { image_urls } : {}),

    ...(att ? { attachment: att } : {}),

    ...(n.session_doc_path ? { session_doc_path: _trimStr(n.session_doc_path) } : {}),
    assist_mode: _trimStr(n.assist_mode) || "B",
    client_message_id: _trimStr(n.client_message_id) || _uuidLike(),
    meta: metaObjOrEmpty(n.meta),

    ...(n.text ? { text: n.text, instruction: n.text } : {}),

    ...(n.bucket ? { bucket: _trimStr(n.bucket) } : {}),
    ...(n.object_name ? { object_name: _trimStr(n.object_name) } : {}),

    ...(allAttUrls.length ? { attachment_file_urls: allAttUrls } : {}),

    ...(n.passthrough || {}),
  };

  _debugAnalyzeRequest({
    route: `/vision/session/${encodeURIComponent(sessionId)}/analyze`,
    sessionId,
    payload: out,
  });

  if (VISION_API_DEBUG) {
    _dbg("[VISION_API][ANALYZE][COUNTS_ONLY]", {
      sessionId: _trimStr(sessionId),
      frame_id_present: !!outboundFrameId,
      image_url_present: !!outboundImageUrl,
      frame_ids_count: frame_ids.length,
      image_urls_count: image_urls.length,
      attachment_fileUrls_count: allAttUrls.length,
      user_text_len: _safeStr(n.text).length,
    });
  }

  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/analyze`,
    { method: "POST", body: JSON.stringify(out) },
    extra
  );
}



function normalizeAction(action) {
  const a = String(action || "").toLowerCase().trim();
  if (a === "analyze") return "analyze";
  return "chat";
}

function deriveAttachmentBits({ imageUrl = "", attachment = null } = {}) {
  let derivedImageUrl = String(imageUrl || "").trim();

  const att = attachment && typeof attachment === "object" ? attachment : null;

  if (!derivedImageUrl && att) {
    derivedImageUrl =
      String(
        att.image_url ||
          att.imageUrl ||
          att.screenshot_url ||
          att.screenshotUrl ||
          att.url ||
          att.fileUrl ||
          att.file_url ||
          ""
      ).trim() || "";
  }

  const derivedFrameId = att ? _coalesceStr(att.frame_id, att.frameId) : "";
  const derivedFrameIds = att ? _uniqStrArray(att.frame_ids || att.frameIds || []) : [];
  const derivedSessionDocPath = att ? _coalesceStr(att.session_doc_path, att.sessionDocPath) : "";

  const fileUrls =
    att && Array.isArray(att.fileUrls)
      ? _uniqStrArray(att.fileUrls)
      : att && att.fileUrl
      ? [String(att.fileUrl).trim()].filter(Boolean)
      : att && att.file_url
      ? [String(att.file_url).trim()].filter(Boolean)
      : [];

  const outAttachment = att
    ? {
        fileUrl: att.fileUrl || att.file_url || null,
        fileUrls: fileUrls,
        fileName: att.fileName || att.file_name || att.name || att.file?.name || null,
        previewUrl: att.previewUrl || att.preview_url || null,
        mime: att.mime || att.content_type || att.file?.type || null,

        image_url: _coalesceStr(att.image_url, att.imageUrl) || null,
        frame_id: derivedFrameId || null,
        frame_ids: derivedFrameIds || [],
        session_doc_path: derivedSessionDocPath || null,
      }
    : null;

  return {
    derivedImageUrl,
    derivedFrameId,
    derivedFrameIds,
    derivedSessionDocPath,
    outAttachment,
    derivedFileUrls: fileUrls,
  };
}

export async function sendVisionChatMessage(
  {
    sessionId,
    text = "",
    imageUrl = "",
    attachment = null,
    assistMode = "B",
    action = "chat",
    meta = {},
    frame_id = "",
    frame_ids = [],
    image_urls = [],
    session_doc_path = "",
    client_message_id = "",
    forceAnalyze = false,
  },
  extra = {}
) {
  if (!sessionId) throw new Error("Missing sessionId.");

  const {
    derivedImageUrl,
    derivedFrameId,
    derivedFrameIds,
    derivedSessionDocPath,
    outAttachment,
    derivedFileUrls,
  } = deriveAttachmentBits({ imageUrl, attachment });

  const finalFrameId = _coalesceStr(frame_id, derivedFrameId);
  const mergedFrameIds = _uniqStrArray([...(Array.isArray(frame_ids) ? frame_ids : []), ...derivedFrameIds]);

  const mergedImageUrls = _uniqStrArray([...(Array.isArray(image_urls) ? image_urls : []), ...(derivedFileUrls || [])]);

  const finalSessionDocPath = _coalesceStr(session_doc_path, derivedSessionDocPath);
  const finalClientMsgId = _coalesceStr(client_message_id) || _uuidLike();

  const actionNorm = normalizeAction(action);
  const metaObj = _isPlainObject(meta) ? meta : {};
  const clientCreatedAt = _pickClientCreatedAt(metaObj);

  if (forceAnalyze || actionNorm === "analyze") {
    const analyzePayload = {
      // Plural analyze targets win over singular compatibility fields.
      // This keeps multi-screen/image requests unambiguous end-to-end.
      image_url: mergedImageUrls.length
        ? undefined
        : (_trimStr(derivedImageUrl) || undefined),
      frame_id: mergedFrameIds.length
        ? undefined
        : (_trimStr(finalFrameId) || undefined),
      session_doc_path: _trimStr(finalSessionDocPath) || undefined,

      frame_ids: mergedFrameIds.length ? mergedFrameIds : undefined,
      image_urls: mergedImageUrls.length ? mergedImageUrls : undefined,

      assist_mode: assistMode || "B",
      client_message_id: finalClientMsgId,

      text: String(text || ""),
      instruction: String(text || ""),

      attachment: outAttachment || undefined,
      meta: {
        ...(metaObj || {}),
        ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
      },

      ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
    };

    for (const k of Object.keys(analyzePayload)) {
      if (typeof analyzePayload[k] === "undefined") delete analyzePayload[k];
    }

    _debugAnalyzeRequest({
      route: `/vision/session/${encodeURIComponent(sessionId)}/analyze`,
      sessionId,
      payload: analyzePayload,
    });

    if (VISION_API_DEBUG) {
      _dbg("[VISION_API][ANALYZE][DERIVED]", {
        sessionId: _trimStr(sessionId),
        assistMode: assistMode || "B",
        frame_id: mergedFrameIds.length ? null : (_trimStr(finalFrameId) || null),
        frame_ids_count: _countArray(mergedFrameIds),
        image_urls_count: _countArray(mergedImageUrls),
        attachment_fileUrls_count: _countArray(outAttachment?.fileUrls),
        attachment_fileUrl: outAttachment?.fileUrl ? _redactUrl(outAttachment.fileUrl) : null,
        derived_image_url: derivedImageUrl ? _redactUrl(derivedImageUrl) : null,
        user_text_len: _safeStr(text).length,
        user_text_preview: _previewText(text, 180),
      });
    }

    return analyzeVisionFrame(sessionId, analyzePayload, extra);
  }

  const payload = {
    text: String(text || ""),
    message: String(text || ""),
    prompt: String(text || ""),

    action: "chat",
    assist_mode: assistMode || "B",

    image_url: _trimStr(derivedImageUrl) || "",
    frame_id: finalFrameId || "",
    session_doc_path: finalSessionDocPath || "",
    client_message_id: finalClientMsgId,

    ...(mergedFrameIds.length ? { frame_ids: mergedFrameIds } : {}),
    ...(mergedImageUrls.length ? { image_urls: mergedImageUrls } : {}),

    attachment: outAttachment,
    meta: metaObj || {},
    ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
  };

  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/chat`,
    { method: "POST", body: JSON.stringify(payload) },
    extra
  );
}

export async function sendVisionClientEvent(
  { sessionId, eventType, assistMode = "B", meta = {}, client_message_id = "" },
  extra = {}
) {
  if (!sessionId) throw new Error("Missing sessionId.");
  if (!eventType) throw new Error("Missing eventType.");

  const metaObj = _isPlainObject(meta) ? meta : {};
  const clientCreatedAt = _pickClientCreatedAt(metaObj);

  const payload = {
    text: `[event:${String(eventType)}]`,
    message: `[event:${String(eventType)}]`,
    prompt: `[event:${String(eventType)}]`,
    action: "chat",
    assist_mode: assistMode || "B",
    client_message_id: _coalesceStr(client_message_id) || _uuidLike(),
    meta: {
      ...(metaObj || {}),
      event_type: String(eventType),
      client_event: true,
      ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
    },
    ...(clientCreatedAt ? { client_created_at: clientCreatedAt } : {}),
  };

  return authedFetch(
    `/vision/session/${encodeURIComponent(sessionId)}/chat`,
    { method: "POST", body: JSON.stringify(payload) },
    extra
  );
}

export async function sendVisionMessage(args, extra = {}) {
  return sendVisionChatMessage(args, extra);
}

/** Default export */
const visionAssistApi = {
  visionSessionLogin,
  visionSessionLogout,

  startVisionSession,
  getVisionSession,
  getVisionSessionWithPaths,
  resolveVisionFirestorePaths,
  pingVisionSession,
  pauseVisionSession,
  resumeVisionSession,
  stopVisionSession,
  getVisionUsage,

  listVisionSessions,
  getVisionMessages,
  getVisionFrameUrl,
  listVisionFrames,
  exportVisionTranscriptHtml,
  exportVisionTranscriptPdf,
  openVisionTranscriptPrintWindow,
  downloadVisionTranscriptHtml,
  downloadVisionTranscriptPdf,

  uploadVisionFrame,
  analyzeVisionFrame,

  sendVisionChatMessage,
  sendVisionMessage,
  sendVisionClientEvent,
};

export default visionAssistApi;
